import React, { useContext, useEffect, useState } from "react";
import { NavLink as RouterLink } from "react-router-dom";

import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import { Badge } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import QuestionAnswerOutlinedIcon from "@material-ui/icons/QuestionAnswerOutlined";
import SendIcon from "@material-ui/icons/Send";
import AssessmentOutlinedIcon from "@material-ui/icons/AssessmentOutlined";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";

const useStyles = makeStyles((theme) => ({
  section: {
    color: theme.palette.type === "dark" ? "#9aa5b8" : "#768198",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: "0.12em",
    lineHeight: "30px",
    paddingLeft: theme.spacing(3),
    textTransform: "uppercase",
  },
  item: {
    borderRadius: 8,
    color: theme.palette.text.primary,
    margin: theme.spacing(0.25, 1),
    minHeight: 42,
    paddingLeft: theme.spacing(1.5),
    "& .MuiListItemIcon-root": {
      color: theme.palette.type === "dark" ? "#b8c1d2" : "#64748b",
      minWidth: 38,
    },
    "& .MuiListItemText-primary": {
      fontSize: 14,
      fontWeight: 600,
    },
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(90, 116, 255, 0.16)" : "#eef3ff",
    },
  },
  active: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(91, 115, 255, 0.25)" : "#e7edff",
    color: "#3154f5",
    "& .MuiListItemIcon-root": {
      color: "#3154f5",
    },
  },
  divider: {
    margin: theme.spacing(1.25, 2, 0.5),
  },
}));

function ListItemLink(props) {
  const { icon, primary, to, className, activeClassName } = props;

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink exact to={to} activeClassName={activeClassName} ref={ref} {...itemProps} />
      )),
    [to]
  );

  return (
    <li>
      <ListItem button component={renderLink} className={className}>
        {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
        <ListItemText primary={primary} />
      </ListItem>
    </li>
  );
}

const MainListItems = (props) => {
  const classes = useStyles();
  const { drawerClose } = props;
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const [connectionWarning, setConnectionWarning] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  return (
    <div onClick={drawerClose}>
      <ListItemLink
        to="/"
        primary="Dashboard"
        icon={<DashboardOutlinedIcon />}
        className={classes.item}
        activeClassName={classes.active}
      />
      <ListItemLink
        to="/connections"
        primary={i18n.t("mainDrawer.listItems.connections")}
        icon={
          <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
            <SyncAltIcon />
          </Badge>
        }
        className={classes.item}
        activeClassName={classes.active}
      />
      <ListItemLink
        to="/tickets"
        primary="Bandeja"
        icon={<WhatsAppIcon />}
        className={classes.item}
        activeClassName={classes.active}
      />
      <ListSubheader disableSticky className={classes.section}>Clientes</ListSubheader>
      <ListItemLink
        to="/contacts"
        primary={i18n.t("mainDrawer.listItems.contacts")}
        icon={<ContactPhoneOutlinedIcon />}
        className={classes.item}
        activeClassName={classes.active}
      />
      <ListItemLink
        to="/quickAnswers"
        primary={i18n.t("mainDrawer.listItems.quickAnswers")}
        icon={<QuestionAnswerOutlinedIcon />}
        className={classes.item}
        activeClassName={classes.active}
      />
      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            <Divider className={classes.divider} />
            <ListSubheader disableSticky className={classes.section}>
              {i18n.t("mainDrawer.listItems.administration")}
            </ListSubheader>
            <ListItemLink
              to="/users"
              primary={i18n.t("mainDrawer.listItems.users")}
              icon={<PeopleAltOutlinedIcon />}
              className={classes.item}
              activeClassName={classes.active}
            />
            <ListItemLink
              to="/queues"
              primary={i18n.t("mainDrawer.listItems.queues")}
              icon={<AccountTreeOutlinedIcon />}
              className={classes.item}
              activeClassName={classes.active}
            />
            <ListItemLink
              to="/campaigns"
              primary="Campañas"
              icon={<SendIcon />}
              className={classes.item}
              activeClassName={classes.active}
            />
            <ListItemLink
              to="/reports"
              primary="Informes"
              icon={<AssessmentOutlinedIcon />}
              className={classes.item}
              activeClassName={classes.active}
            />
            <ListItemLink
              to="/settings"
              primary={i18n.t("mainDrawer.listItems.settings")}
              icon={<SettingsOutlinedIcon />}
              className={classes.item}
              activeClassName={classes.active}
            />
          </>
        )}
      />
    </div>
  );
};

export default MainListItems;
